/**
 * Enemy pool, integration and lifecycle.
 *
 * Allocation policy: every enemy object, every damage-over-time record and
 * every effect payload this file hands to `world.fx` comes out of a ring or a
 * free list that was built at module load. A steady-state round spawning four
 * hundred swarmlings and killing them all allocates nothing.
 *
 * Motion policy: flocking families get their steering from `Flock.ts` and are
 * integrated here as free bodies. Everything else rides the spline, but never
 * exactly on it — each instance carries a lateral lane, a slow noise drift and
 * (for weavers) a phase-varied sine across the corridor. A column of grunts
 * has to look like a column of grunts, not a conga line.
 */

import { ECONOMY, VIEW } from '@/core/config';
import { ROLE } from '@/core/palette';
import type { PhosphorName } from '@/core/palette';
import type {
  DamageType, Enemy, EnemyArchetype, EnemyFamily, ParticleSpawn, System, World,
} from '@/core/types';
import { enemyById, motionOf, type MotionProfile } from '@/game/EnemyCatalog';
import {
  getActiveTrack, makeTrackSample, type Track, type TrackSample,
} from '@/game/Track';

const TAU = Math.PI * 2;

/**
 * Fields the enemy sim needs but that no other subsystem may touch. Structural
 * typing means these objects still satisfy `Enemy` everywhere else.
 */
export interface EnemyInternal extends Enemy {
  /** Index into the backing pool. Stable for the object's whole life. */
  slot: number;
  /** Target lateral position as a fraction of corridor half-width. */
  lane: number;
  /** Smoothed lateral position, so a lane change does not snap. */
  laneNow: number;
  /** Weave phase in radians. */
  phase: number;
  /** Seed for this instance's value noise. */
  seed: number;
  /** Per-instance speed multiplier. */
  speedVar: number;
  /** Angular velocity of the banking spring. */
  omega: number;
  /** Hit-point multiplier inherited from the wave curve; passed to children. */
  hpScale: number;
  /** Sim time of the last damage taken. Gates shield regeneration. */
  lastHit: number;
  /** Generation in a splitter chain. */
  depth: number;
  /** Neighbour candidates seen last flock step; drives the sampling stride. */
  neighbourEst: number;
  /** Steering cache used by the flock solver. */
  flowBlend: number;
  /** True while the object sits on the free list. Guards a double release. */
  pooled: boolean;
}

// ─────────────────────────────────────────────────────────────────── pool ──

const POOL_LIMIT = 6000;
const pool: EnemyInternal[] = [];
const free: EnemyInternal[] = [];
let nextId = 1;

function makeEnemy(slot: number): EnemyInternal {
  return {
    alive: false,
    id: 0,
    archetype: PLACEHOLDER,
    pos: { x: 0, y: 0 },
    vel: { x: 0, y: 0 },
    acc: { x: 0, y: 0 },
    progress: 0,
    hp: 1,
    maxHp: 1,
    shield: 0,
    heading: 0,
    slowUntil: 0,
    slowFactor: 1,
    stunUntil: 0,
    dots: [],
    flash: 0,
    birth: 0,
    wave: 0,
    slot,
    lane: 0,
    laneNow: 0,
    phase: 0,
    seed: 0,
    speedVar: 1,
    omega: 0,
    hpScale: 1,
    lastHit: -999,
    depth: 0,
    neighbourEst: 8,
    flowBlend: 0,
    pooled: true,
  };
}

/** Only ever referenced before the first `acquire`; never simulated. */
const PLACEHOLDER: EnemyArchetype = {
  id: '__none', family: 'grunt', glyph: 'grunt', colour: 'gray',
  hp: 1, speed: 1, armour: 0, shield: 0, shieldRegenPerSec: 0,
  leak: 0, bounty: 0, radius: 1, flocks: false,
};

/** Preallocates the pool. Call once at boot with the worst-case round size. */
export function reserveEnemies(n: number): void {
  const want = Math.min(POOL_LIMIT, n);
  while (pool.length < want) {
    const e = makeEnemy(pool.length);
    pool.push(e);
    free.push(e);
  }
}

reserveEnemies(1024);

// ───────────────────────────────────────────────────────── dot free list ──

interface Dot { dps: number; until: number; type: DamageType }
const dotFree: Dot[] = [];
for (let i = 0; i < 512; i++) dotFree.push({ dps: 0, until: 0, type: 'kinetic' });

/**
 * Adds a damage-over-time stack without allocating. Towers should call this
 * rather than pushing onto `enemy.dots` directly.
 */
export function applyDot(e: Enemy, dps: number, seconds: number, type: DamageType, now: number): void {
  if (e.archetype.immune?.includes('dot')) return;
  const d = dotFree.pop() ?? { dps: 0, until: 0, type };
  d.dps = dps;
  d.until = now + seconds;
  d.type = type;
  e.dots.push(d);
}

function clearDots(e: Enemy): void {
  for (let i = e.dots.length - 1; i >= 0; i--) {
    const d = e.dots[i] as Dot;
    e.dots.pop();
    if (dotFree.length < 512) dotFree.push(d);
  }
}

// ──────────────────────────────────────────────────────────── fx recycling ──

const PARTICLE_RING = 4096;
const SHOCK_RING = 256;
const NUMBER_RING = 512;

const particleRing: ParticleSpawn[] = new Array(PARTICLE_RING);
for (let i = 0; i < PARTICLE_RING; i++) {
  particleRing[i] = {
    x: 0, y: 0, vx: 0, vy: 0, life: 0, size: 1,
    colour: 'white', gain: 1, kind: 'spark', drag: 0, gravity: 0, spin: 0,
  };
}
const shockRing = new Array(SHOCK_RING).fill(null).map(() => ({ x: 0, y: 0, strength: 0, radius: 0, duration: 0 }));
const numberRing = new Array(NUMBER_RING).fill(null).map(() => ({ x: 0, y: 0, value: 0, crit: false }));

let pRing = 0, sRing = 0, nRing = 0;

function emit(
  world: World, x: number, y: number, vx: number, vy: number,
  life: number, size: number, colour: ParticleSpawn['colour'],
  kind: ParticleSpawn['kind'], gain: number, drag: number, gravity: number, spin: number,
): void {
  const p = particleRing[pRing];
  pRing = (pRing + 1) & (PARTICLE_RING - 1);
  p.x = x; p.y = y; p.vx = vx; p.vy = vy;
  p.life = life; p.size = size; p.colour = colour; p.kind = kind;
  p.gain = gain; p.drag = drag; p.gravity = gravity; p.spin = spin;
  world.fx.particles.push(p);
}

function shock(world: World, x: number, y: number, strength: number, radius: number, duration: number): void {
  const s = shockRing[sRing];
  sRing = (sRing + 1) % SHOCK_RING;
  s.x = x; s.y = y; s.strength = strength; s.radius = radius; s.duration = duration;
  world.fx.shockwaves.push(s);
}

/** Queues floating combat text. Exposed so towers can show crits. */
export function emitNumber(world: World, x: number, y: number, value: number, crit: boolean): void {
  const n = numberRing[nRing];
  nRing = (nRing + 1) % NUMBER_RING;
  n.x = x; n.y = y; n.value = value; n.crit = crit;
  world.fx.numbers.push(n);
}

// ───────────────────────────────────────────────────────────────── hooks ──

/**
 * Hit-point scaling by round **and archetype**. `Waves.ts` owns the real curves;
 * this default exists so the sim is playable standalone. Replace it, do not
 * fork it.
 *
 * The archetype argument is not decoration. `EnemyCatalog` authors bosses as
 * absolute end-game statlines (SINGULARITY at 42,000hp) and everything else as
 * round-1 bases (grunt at 40hp), so one curve cannot serve both:
 * `Waves.bossHpScale` solves each boss against `BOSS_RBE` while `Waves.hpScale`
 * runs to ~940x. Handed only the round, this hook scaled round 100's boss by the
 * *trash* curve — 39 million hit points behind 12 million of overshield
 * regenerating at 600,000 a second — and no board in the game could remove a
 * point of it. `main.ts` carried a `rescaleSpawns` pass to correct the tail of
 * the array after every spawn; with the id passed through there is nothing left
 * for it to correct.
 */
export let waveHpScale: (round: number, archetypeId: string) => number =
  (r) => (r <= 1 ? 1 : Math.pow(1.058, r - 1) + (r - 1) * 0.05);

export function setWaveHpScale(fn: (round: number, archetypeId: string) => number): void {
  waveHpScale = fn;
}

/**
 * Cash awarded for a kill. Default reads `ECONOMY.cashPerHp` literally, which
 * makes late rounds very rich; the economy owner is expected to override it.
 */
export let cashForKill: (e: Enemy) => number =
  (e) => Math.max(1, Math.round(e.maxHp * e.archetype.bounty * ECONOMY.cashPerHp));

export function setCashForKill(fn: (e: Enemy) => number): void {
  cashForKill = fn;
}

// ──────────────────────────────────────────────────────── acquire/release ──

const sample: TrackSample = makeTrackSample();

/**
 * Pulls an enemy from the free list, seeds its per-instance variation and
 * pushes it into the world. `progress` and `lane` are only passed by the
 * splitter path, which needs children placed on top of their parent.
 */
export function acquire(
  archetype: EnemyArchetype,
  world: World,
  progress = 0,
  lane = NaN,
  hpScale = NaN,
  depth = 0,
): EnemyInternal | null {
  let e = free.pop();
  if (!e) {
    if (pool.length >= POOL_LIMIT) return null;
    e = makeEnemy(pool.length);
    pool.push(e);
  }

  const rng = world.rng;
  const mp = motionOf(archetype.id);
  const scale = Number.isNaN(hpScale) ? waveHpScale(world.round, archetype.id) : hpScale;

  e.alive = true;
  e.pooled = false;
  e.id = nextId++;
  e.archetype = archetype;
  e.maxHp = Math.max(1, Math.round(archetype.hp * scale));
  e.hp = e.maxHp;
  e.shield = archetype.shield > 0 ? Math.round(archetype.shield * scale) : 0;
  e.hpScale = scale;
  e.progress = progress;
  e.heading = 0;
  e.omega = 0;
  e.slowUntil = 0;
  e.slowFactor = 1;
  e.stunUntil = 0;
  e.flash = 0;
  e.birth = 0;
  e.wave = world.round;
  e.lastHit = -999;
  e.depth = depth;
  e.neighbourEst = 8;
  e.flowBlend = 0;
  e.acc.x = 0;
  e.acc.y = 0;
  clearDots(e);

  e.seed = ((rng() * 0x7fffffff) | 0) >>> 0;
  e.phase = rng() * TAU;
  e.speedVar = 1 + (rng() * 2 - 1) * mp.speedJitter;
  e.lane = Number.isNaN(lane) ? (rng() * 2 - 1) * mp.laneSpread : lane;
  e.laneNow = e.lane;

  const track = getActiveTrack();
  if (track) {
    track.sampleExtended(progress, sample);
    const off = e.laneNow * Math.max(0, sample.w - archetype.radius * 0.6);
    e.pos.x = sample.x - sample.ty * off;
    e.pos.y = sample.y + sample.tx * off;
    e.vel.x = sample.tx * archetype.speed * e.speedVar;
    e.vel.y = sample.ty * archetype.speed * e.speedVar;
    e.heading = Math.atan2(e.vel.y, e.vel.x);
  } else {
    e.pos.x = 0; e.pos.y = 0;
    e.vel.x = archetype.speed; e.vel.y = 0;
  }

  world.enemies.push(e);
  return e;
}

/** Returns an enemy to the free list. Safe to call twice. */
export function release(e: Enemy): void {
  const ei = e as EnemyInternal;
  if (ei.pooled) return;
  ei.pooled = true;
  ei.alive = false;
  clearDots(ei);
  ei.archetype = PLACEHOLDER;
  free.push(ei);
}

/** Kills everything and returns it to the pool. Used by the scenario harness. */
export function resetEnemies(world: World): void {
  for (let i = world.enemies.length - 1; i >= 0; i--) release(world.enemies[i]);
  world.enemies.length = 0;
  resetDeathFx();
}

export const enemyPoolStats = (): { total: number; free: number } =>
  ({ total: pool.length, free: free.length });

// ───────────────────────────────────────────────────────────────── damage ──

/**
 * Applies one hit. Shield absorbs first, then armour reduces per hit, so a
 * high-rate low-damage tower is throttled twice against an armoured target.
 * Returns the health actually removed, for tower damage counters.
 */
export function damageEnemy(e: Enemy, amount: number, type: DamageType, world: World): number {
  if (!e.alive || amount <= 0) return 0;
  const a = e.archetype;
  const mult = a.resist?.[type] ?? 1;
  let dmg = amount * mult;

  const ei = e as EnemyInternal;
  ei.lastHit = world.time;
  e.flash = 1;

  if (e.shield > 0) {
    const absorbed = Math.min(e.shield, dmg);
    e.shield -= absorbed;
    dmg -= absorbed;
    if (e.shield <= 0) {
      e.shield = 0;
      // Shield break is a readable, loud moment: a ring plus a hard spark burst.
      shock(world, e.pos.x, e.pos.y, 26, a.radius * 5, 0.16);
      for (let i = 0; i < 10; i++) {
        const ang = (i / 10) * TAU;
        emit(world, e.pos.x, e.pos.y, Math.cos(ang) * 70, Math.sin(ang) * 70,
          0.32, 1.5, ROLE.shielded, 'ring', 1.5, 3.2, 0, 0);
      }
    }
    if (dmg <= 0) return 0;
  }

  dmg -= a.armour;
  // Nothing is ever truly immune; a floor keeps chip damage meaningful without
  // undoing the point of armour.
  if (dmg < amount * 0.05) dmg = amount * 0.05;

  const dealt = Math.min(e.hp, dmg);
  e.hp -= dmg;
  return dealt;
}

// ──────────────────────────────────────────────────────────────── stepping ──

let leakPos = { x: 0, y: 0 };

/** Moves due spawns out of `world.pending` and into the world. */
export function spawnPending(world: World): void {
  const q = world.pending;
  for (let i = q.length - 1; i >= 0; i--) {
    const p = q[i];
    if (p.at > world.time) continue;
    const arch = enemyById(p.archetypeId);
    const mp = motionOf(arch.id);
    // A cluster enters as a blob: wide lateral scatter and a short tail of
    // progress so forty bodies do not stack on one pixel and explode apart.
    const lane = p.cluster
      ? (world.rng() * 2 - 1) * Math.max(0.7, mp.laneSpread)
      : NaN;
    const back = p.cluster ? world.rng() * 96 : 0;
    acquire(arch, world, -back, lane);
    q[i] = q[q.length - 1];
    q.pop();
  }
}

/**
 * One simulation step. Runs *after* `stepFlock`, which fills `acc` for the
 * flocking families and leaves it zero for everyone else.
 */
export function stepEnemies(dt: number, world: World, trackArg?: Track): void {
  const track = trackArg ?? getActiveTrack();
  if (!track) return;

  spawnPending(world);
  // Before anything moves: a sub-burst booked two frames ago belongs to the
  // body that was standing there then, not to wherever the survivors are now.
  stepDeathSchedule(world);

  const list = world.enemies;
  const now = world.time;

  for (let i = list.length - 1; i >= 0; i--) {
    const e = list[i] as EnemyInternal;

    // Killed from outside this file — `Projectiles.resolve` is the only writer
    // of `alive = false` that leaves the body in the list. Its effects are
    // already drawn; what it cannot do from over there is the bookkeeping.
    if (!e.alive) { settleKill(e, world, track); swapOut(list, i); release(e); continue; }

    const a = e.archetype;
    const mp = motionOf(a.id);

    if (e.birth < 1) {
      e.birth += dt * 4;
      if (e.birth > 1) e.birth = 1;
    }
    if (e.flash > 0) {
      e.flash -= dt * 9;
      if (e.flash < 0) e.flash = 0;
    }

    // ── status ──
    const stunned = e.stunUntil > now && !a.immune?.includes('stun');
    let slow = 1;
    if (e.slowUntil > now && !a.immune?.includes('slow')) slow = e.slowFactor;

    // ── damage over time ──
    for (let d = e.dots.length - 1; d >= 0; d--) {
      const dot = e.dots[d] as Dot;
      if (dot.until <= now) {
        e.dots[d] = e.dots[e.dots.length - 1];
        e.dots.pop();
        if (dotFree.length < 512) dotFree.push(dot);
        continue;
      }
      const mult = a.resist?.[dot.type] ?? 1;

      // Armour is a flat reduction *per hit*. A damage-over-time tick is not a
      // hit, so it is reduced per second and only then scaled by dt. Routing
      // the tick through `damageEnemy` instead would subtract the whole armour
      // value from a 1/60th-second slice and zero every DOT against any
      // armoured target at all.
      let dps = dot.dps * mult - a.armour;
      const floor = dot.dps * mult * 0.05;
      if (dps < floor) dps = floor;

      let amount = dps * dt;

      // An overshield absorbs corrosion the same as it absorbs a bullet.
      // Writing straight to `hp` let acid burn through the shielded family's
      // one defining mechanic.
      if (e.shield > 0) {
        const absorbed = Math.min(e.shield, amount);
        e.shield -= absorbed;
        amount -= absorbed;
        if (e.shield <= 0) e.shield = 0;
      }
      if (amount > 0) e.hp -= amount;

      // `lastHit` is deliberately not refreshed here. A DOT ticks every frame,
      // so refreshing it held `now - lastHit > SHIELD_REGEN_DELAY` false
      // forever: one stack of the cheapest acid permanently disabled shield
      // regeneration, which is a stronger effect than anything the upgrade
      // tree actually sells. Regeneration now races the burn instead.
    }

    // ── shield regeneration ──
    if (a.shield > 0) {
      const cap = Math.round(a.shield * e.hpScale);
      if (e.shield < cap && now - e.lastHit > SHIELD_REGEN_DELAY) {
        e.shield += a.shieldRegenPerSec * e.hpScale * dt;
        if (e.shield > cap) e.shield = cap;
      }
    }

    if (e.hp <= 0) {
      killEnemy(e, world, track);
      swapOut(list, i);
      release(e);
      continue;
    }

    // ── motion ──
    if (a.flocks) integrateFlocker(e, dt, track, slow, stunned);
    else followSpline(e, dt, world, track, mp, slow, stunned);

    updateHeading(e, dt, mp);

    // ── leak ──
    if (e.progress >= track.length) {
      track.sampleAt(track.length, sample);
      leakPos.x = sample.x;
      leakPos.y = sample.y;
      leakEnemy(e, world);
      swapOut(list, i);
      release(e);
    }
  }

  world.tick++;
}

/**
 * Seconds without damage before an overshield starts coming back.
 *
 * Exported because `Balance.ts` needs it: a body being hit more often than this
 * never regenerates, so the number of bodies a board can *suppress* is its rate
 * of hits per second times this window. That is the whole of the shielded
 * family's difficulty and the model cannot price it without this constant.
 */
export const SHIELD_REGEN_DELAY = 0.85;

function swapOut(list: Enemy[], i: number): void {
  list[i] = list[list.length - 1];
  list.pop();
}

// ──────────────────────────────────────────────────────────── spline path ──

/**
 * Rides the centreline with a lateral offset. The offset has three parts: a
 * fixed per-instance lane, a slow noise drift, and a weave sine for the weaver
 * family. Without the first two a wave of grunts reads as a single-file queue,
 * which is the tell that separates a toy from a game.
 */
function followSpline(
  e: EnemyInternal, dt: number, world: World, track: Track,
  mp: MotionProfile, slow: number, stunned: boolean,
): void {
  const a = e.archetype;
  const px = e.pos.x, py = e.pos.y;

  if (!stunned) e.progress += a.speed * e.speedVar * slow * dt;

  track.sampleExtended(e.progress, sample);

  const drift = noise1(e.seed, world.time * 0.22) * 2 - 1;
  let frac = e.lane + drift * 0.22;
  if (mp.weaveAmp > 0) {
    frac += Math.sin(world.time * mp.weaveHz * TAU + e.phase) * mp.weaveAmp;
  }
  if (frac > 0.95) frac = 0.95; else if (frac < -0.95) frac = -0.95;

  // Ease toward the target lane so a weave reversal is a curve, not a corner.
  e.laneNow += (frac - e.laneNow) * Math.min(1, 7 * dt);

  const room = Math.max(0, sample.w - a.radius * 0.6);
  const off = e.laneNow * room;
  e.pos.x = sample.x - sample.ty * off;
  e.pos.y = sample.y + sample.tx * off;

  const inv = dt > 0 ? 1 / dt : 0;
  e.vel.x = (e.pos.x - px) * inv;
  e.vel.y = (e.pos.y - py) * inv;
}

// ───────────────────────────────────────────────────────── free-body path ──

/** Speed a flocker may reach, above its nominal pace, chasing the swarm. */
const FLOCK_SPEED_HEADROOM = 1.42;
/** How far behind the entry a cluster may queue up, in pixels. */
export const SPAWN_RUNWAY = 220;
/**
 * Arc-length window for the progress re-sync. Wide enough to catch up with a
 * badly jostled body, far narrower than the arc distance between any two
 * strands that share screen space on the shipped maps.
 */
const PROGRESS_WINDOW = 90;

function integrateFlocker(
  e: EnemyInternal, dt: number, track: Track, slow: number, stunned: boolean,
): void {
  const a = e.archetype;

  if (stunned) {
    e.vel.x *= 0.82;
    e.vel.y *= 0.82;
    e.acc.x = 0;
    e.acc.y = 0;
    return;
  }

  e.vel.x += e.acc.x * dt;
  e.vel.y += e.acc.y * dt;
  e.acc.x = 0;
  e.acc.y = 0;

  // The solver steers toward a desired velocity, so damping lives there. All
  // that is left here is a ceiling for the overshoot a wall push can cause.
  const cap = a.speed * e.speedVar * slow * FLOCK_SPEED_HEADROOM;
  const sp = Math.hypot(e.vel.x, e.vel.y);
  if (sp > cap && sp > 1e-4) {
    const k = cap / sp;
    e.vel.x *= k;
    e.vel.y *= k;
  }

  e.pos.x += e.vel.x * dt;
  e.pos.y += e.vel.y * dt;
  clampToField(e);

  // Progress needs both halves. Dead reckoning along the tangent is smooth and
  // survives being jostled sideways; on its own it drifts out of sync with the
  // body inside a dense crowd, and an agent whose target sits behind it stalls
  // for good. So the reckoning seeds a window-limited re-sync against the real
  // geometry, and the correction is rate-limited to the agent's own pace — it
  // converges, and it can never skip a length of track it did not walk.
  track.sampleExtended(e.progress, sample);
  const along = e.vel.x * sample.tx + e.vel.y * sample.ty;
  let p = e.progress + along * dt;

  if (p > 0) {
    const snap = track.localProgress(e.pos.x, e.pos.y, p, PROGRESS_WINDOW);
    const limit = cap * 1.5 * dt;
    let d = snap - p;
    if (d > limit) d = limit; else if (d < -limit) d = -limit;
    p += d;
  } else {
    // Behind the start line the re-sync above has nothing to search — the
    // centreline does not extend there and `localProgress` clamps its window to
    // zero — so a body in the spawn runway used to run on dead reckoning alone.
    // Dead reckoning never disagrees with itself, which is the whole problem: a
    // cluster body whose *position* was moved by something other than its own
    // velocity keeps a progress that says it is somewhere it is not. On vortex
    // and maelstrom the entry sits 60px off-screen and a cluster enters up to
    // 96px behind that, so `clampToField` and the soft wall in `Flock.stepFlock`
    // both move it and neither tells it. `stepFlock` then reads the stale
    // progress for the path target *and* for the containment spring, the two
    // point opposite ways, the body settles at zero velocity, `along` goes to
    // zero, and the progress is frozen for good. `WaveDirector.step` ends a
    // round only when nothing is alive, so that is a run that cannot advance:
    // docs/BALANCE.md §7.7 measured seven such bodies on round 14.
    //
    // The entry tail is straight, so there is no search to do. The body's
    // distance along the entry tangent *is* its progress. Measure it.
    track.sampleAt(0, sample);
    p = (e.pos.x - sample.x) * sample.tx + (e.pos.y - sample.y) * sample.ty;
    if (p > 0) p = 0;
  }

  e.progress = p < -SPAWN_RUNWAY ? -SPAWN_RUNWAY : p;
}

// ──────────────────────────────────────────────────────────────── heading ──

/**
 * Banking. The sprite angle is a damped spring chasing the velocity angle, run
 * deliberately under-damped so a hard turn lags on entry and overshoots on
 * exit. That overshoot is the whole reason a Geometry Wars swarm reads as
 * having mass; snap the heading to the velocity and it reads as dots on rails.
 */
function updateHeading(e: EnemyInternal, dt: number, mp: MotionProfile): void {
  const sp2 = e.vel.x * e.vel.x + e.vel.y * e.vel.y;
  if (sp2 < 1e-3) return;
  const target = Math.atan2(e.vel.y, e.vel.x);
  let d = target - e.heading;
  d = d - TAU * Math.floor((d + Math.PI) / TAU);
  e.omega += (d * mp.bankStiffness - e.omega * mp.bankDamping) * dt;
  if (e.omega > 40) e.omega = 40; else if (e.omega < -40) e.omega = -40;
  e.heading += e.omega * dt;
  if (e.heading > Math.PI) e.heading -= TAU;
  else if (e.heading < -Math.PI) e.heading += TAU;
}

// ───────────────────────────────────────────────────────────── death, leak ──

/**
 * The bookkeeping half of a death: the kill hook and the split chain.
 *
 * Split out from {@link killEnemy} because **this file is not the only thing
 * that kills enemies**. `towers/Projectiles.resolve` drops an enemy's hit
 * points to zero itself, flips `alive` and plays its own death effects, and
 * until this existed those deaths fell straight through the `!e.alive` branch
 * in {@link stepEnemies} to `release()` — counted by nothing, paying nothing,
 * shedding no splitter children.
 *
 * The scale of it, measured headlessly over twelve rounds with an eight-tower
 * board: 435 enemies spawned, 435 killed by `resolve`, **zero** reaching
 * `killEnemy`. Three consequences, all of which showed in the 100-round
 * playtest:
 *
 *  - `Session.stats.kills` reads 240 for a hundred rounds, because
 *    `main.ts`'s `setCashForKill` hook is the kill counter and it only fires
 *    from here. The 240 are the damage-over-time kills, the one path that does
 *    reach {@link killEnemy}.
 *  - **The splitter family never splits.** `spawnChildren` is only reachable
 *    from a death, so a `splitter.prime` shot dead is one body rather than
 *    thirty-three, and every RBE number that counted the chain was fiction.
 *  - No death sound plays, ever. The same hook carries the audio cue.
 *
 * Effects stay in {@link killEnemy}: `resolve` has already drawn its own, and
 * routing through here would double every explosion.
 */
function settleKill(e: EnemyInternal, world: World, track: Track): void {
  world.cash += cashForKill(e);
  if (e.archetype.spawnsOnDeath) spawnChildren(e, world, track);
}

function killEnemy(e: EnemyInternal, world: World, track: Track): void {
  deathBurst(world, e);
  settleKill(e, world, track);
}

/**
 * Removes one enemy from the board immediately, with its full death burst and
 * none of the bookkeeping.
 *
 * The defeat cascade's one entry point. No cash is paid, no splitter sheds
 * children and no kill is counted, because the run is already lost and a board
 * that answered a loss by spawning thirty more bodies would still be spawning
 * them behind the sheet. `index` is the body's slot in `world.enemies`; the
 * caller walks the list backwards, which is what makes the swap-out safe.
 */
export function vaporiseAt(world: World, index: number): void {
  const list = world.enemies;
  if (index < 0 || index >= list.length) return;
  const e = list[index] as EnemyInternal;
  if (e.pooled) return;
  deathBurst(world, e);
  swapOut(list, index);
  release(e);
}

// ────────────────────────────────────────────────────────── death variants ──

/*
 * One death used to be one shape: N sparks on a uniform ring, a few debris, one
 * expanding ring, one shockwave. The speeds were random and the *composition*
 * was not, so forty deaths inside a second read as one stamped effect rather
 * than as forty things coming apart. These six compositions are genuinely
 * different silhouettes, and which one an enemy plays is fixed by its own seed
 * and id, so a replayed run dies the same way twice.
 *
 * Colour is never varied: it stays the family's phosphor, which is the identity
 * rule the whole palette is built on. What varies is direction, timing and
 * particle *kind*.
 */

const V_RING = 0;
const V_LANCE = 1;
const V_IMPLODE = 2;
const V_SHATTER = 3;
const V_CRACKLE = 4;
const V_POP = 5;
const VARIANTS = 6;

const FAMILY_ROW: Record<EnemyFamily, number> = {
  grunt: 0, runner: 1, armoured: 2, swarmling: 3,
  weaver: 4, splitter: 5, shielded: 6, boss: 7,
};

/**
 * Weight of each variant per family, sixteenths, row-major by {@link FAMILY_ROW}.
 *
 * The mix is characterisation, not decoration. Runners lance because they die
 * mid-sprint and the burst should keep their momentum. Armour shatters, because
 * a plate coming off is the thing that family is about. Splitters and weavers
 * implode, which pre-figures the children. Swarmlings mostly pop: they die in
 * hundreds and the budget is the design constraint, so their cheap variant is
 * also their commonest. Bosses keep the full ring every time — the one death in
 * the game that has earned a bespoke moment should not be randomised out of it.
 */
const VARIANT_WEIGHTS = new Uint8Array([
  // ring lance implode shatter crackle pop
  5, 4, 2, 3, 2, 0, // grunt
  2, 8, 1, 2, 3, 0, // runner
  4, 2, 2, 7, 1, 0, // armoured
  2, 3, 0, 0, 0, 11, // swarmling
  3, 3, 5, 2, 3, 0, // weaver
  3, 1, 6, 4, 2, 0, // splitter
  6, 2, 3, 3, 2, 0, // shielded
  16, 0, 0, 0, 0, 0, // boss
]);

/**
 * Which composition this body dies in.
 *
 * Seeded from the instance, never from `Math.random` and never from `world.rng`
 * — the shared stream is advanced a different number of times by every variant,
 * so drawing the *choice* from it would make the choice depend on how the last
 * enemy died and a replay would diverge on the first kill.
 */
function pickVariant(e: EnemyInternal): number {
  const row = FAMILY_ROW[e.archetype.family] * VARIANTS;
  let roll = Math.floor(hash2(e.seed ^ 0x5bf03635, e.id) * 16);
  if (roll > 15) roll = 15;
  for (let i = 0; i < VARIANTS; i++) {
    roll -= VARIANT_WEIGHTS[row + i];
    if (roll < 0) return i;
  }
  return V_RING;
}

// ─────────────────────────────────────────────────────── delayed sub-bursts ──

/**
 * Sub-bursts owed to the future, in structure-of-arrays form.
 *
 * The crackle and implode variants are *sequences*, and a sequence needs
 * somewhere to live between the death and its last pop. A closure per death
 * would allocate once per kill at swarm scale; this is the same compacted-pool
 * idiom the dot free list and the particle pool already use, so a death costs
 * one slot write and nothing else. Overflow drops the tail rather than growing.
 */
const SUB_CAP = 192;
const subAt = new Float32Array(SUB_CAP);
const subX = new Float32Array(SUB_CAP);
const subY = new Float32Array(SUB_CAP);
const subSpd = new Float32Array(SUB_CAP);
const subSize = new Float32Array(SUB_CAP);
const subN = new Uint8Array(SUB_CAP);
const subRing = new Uint8Array(SUB_CAP);
const subColour: PhosphorName[] = new Array(SUB_CAP).fill('white');
let subCount = 0;

function scheduleBurst(
  world: World, at: number, x: number, y: number,
  n: number, spd: number, size: number, colour: PhosphorName, ring: number,
): void {
  if (subCount >= SUB_CAP) return;
  const i = subCount++;
  subAt[i] = world.time + at;
  subX[i] = x;
  subY[i] = y;
  subN[i] = n > 255 ? 255 : n;
  subSpd[i] = spd;
  subSize[i] = size;
  subColour[i] = colour;
  subRing[i] = ring;
}

/**
 * Fires every sub-burst that has come due. Runs once per step, before anything
 * else moves, so a scheduled pop lands on the frame it was booked for whatever
 * else happened to the board in between.
 */
function stepDeathSchedule(world: World): void {
  const now = world.time;
  const rng = world.rng;
  let i = 0;
  while (i < subCount) {
    if (subAt[i] > now) { i++; continue; }
    const x = subX[i], y = subY[i], colour = subColour[i];
    const spd = subSpd[i], size = subSize[i];
    const n = subN[i];
    for (let k = 0; k < n; k++) {
      const ang = rng() * TAU;
      const s = spd * (0.35 + rng() * 0.95);
      emit(world, x, y, Math.cos(ang) * s, Math.sin(ang) * s,
        0.14 + rng() * 0.28, size * (0.7 + rng() * 0.7),
        colour, 'spark', 1.5, 3.4, 26, 0);
    }
    if (subRing[i] > 0) {
      emit(world, x, y, 0, 0, 0.2, subRing[i], colour, 'ring', 1.8, 0, 0, 0);
    }
    const j = --subCount;
    if (i !== j) {
      subAt[i] = subAt[j]; subX[i] = subX[j]; subY[i] = subY[j];
      subSpd[i] = subSpd[j]; subSize[i] = subSize[j];
      subN[i] = subN[j]; subRing[i] = subRing[j]; subColour[i] = subColour[j];
    }
  }
}

/** Drops every pending sub-burst. The scenario harness needs a clean board. */
export function resetDeathFx(): void {
  subCount = 0;
}

/** Sub-bursts still owed. Exported for the perf probe and for tests. */
export const pendingDeathFx = (): number => subCount;

// ────────────────────────────────────────────────────────────── the bursts ──

/**
 * Plays a death, composition chosen by {@link pickVariant}.
 *
 * Exported because **this file is not the only thing that kills enemies**:
 * `towers/Projectiles.resolve` draws the death for every shot kill, which is
 * every kill a real board makes. A randomiser wired only into {@link killEnemy}
 * would be invisible in play.
 *
 * The `Enemy` argument is really an {@link EnemyInternal} — every body in the
 * game comes out of the pool in this file — and the cast is what lets callers
 * outside the enemy sim reach the variant seed without the field leaking into
 * the public type.
 */
export function deathBurst(world: World, enemy: Enemy): void {
  const e = enemy as EnemyInternal;
  const a = e.archetype;
  const x = e.pos.x, y = e.pos.y;

  switch (pickVariant(e)) {
    case V_LANCE: burstLance(world, e, x, y); break;
    case V_IMPLODE: burstImplode(world, e, x, y); break;
    case V_SHATTER: burstShatter(world, e, x, y); break;
    case V_CRACKLE: burstCrackle(world, e, x, y); break;
    case V_POP: burstPop(world, e, x, y); break;
    default: burstRing(world, e, x, y); break;
  }

  // The lattice ping and the camera are the two things every death owes,
  // whatever shape it came apart in. `V_POP` pays a fraction of both: four
  // hundred swarmlings dying inside a second would otherwise queue four hundred
  // shockwaves into a 256-deep ring and shake the camera to pieces.
  const pop = a.family === 'swarmling';
  const big = a.radius >= 9;
  shock(world, x, y, (14 + a.radius * 3.4) * (pop ? 0.5 : 1), a.radius * 7 + 18,
    big ? 0.2 : 0.09);
  world.fx.shake += Math.min(3.2, a.radius * (pop ? 0.05 : 0.16));
}

/** Baseline spark count. Every variant is written as a share of it. */
function sparkCount(radius: number): number {
  const n = 6 + Math.round(radius * 2.4);
  return n > 48 ? 48 : n;
}

/** The original: an even shell of shrapnel, chips, and one expanding ring. */
function burstRing(world: World, e: EnemyInternal, x: number, y: number): void {
  const a = e.archetype;
  const rng = world.rng;
  const big = a.radius >= 9;
  const n = sparkCount(a.radius);
  for (let i = 0; i < n; i++) {
    const ang = rng() * TAU;
    const spd = 30 + rng() * (big ? 210 : 120);
    emit(world, x, y, Math.cos(ang) * spd, Math.sin(ang) * spd,
      0.26 + rng() * 0.4, 1 + rng() * (big ? 2.4 : 1.2),
      a.colour, 'spark', 1.4, 2.4, 34, 0);
  }
  for (let i = 0; i < (big ? 14 : 5); i++) {
    const ang = rng() * TAU;
    const spd = 20 + rng() * 90;
    emit(world, x, y, Math.cos(ang) * spd, Math.sin(ang) * spd,
      0.5 + rng() * 0.6, 1 + rng() * 2,
      a.colour, 'debris', 0.9, 1.5, 90, (rng() * 2 - 1) * 12);
  }
  emit(world, x, y, 0, 0, 0.22, a.radius * 1.6, ROLE.explosion, 'ring', 2.2, 0, 0, 0);
}

/**
 * A blow-out along the body's own travel: a fast forward cone, a thin
 * back-splash, and a ring that opens behind it. The enemy's velocity is the
 * honest proxy for the killing blow — the projectile that landed is already
 * gone by the time anything here runs, and a death that flies the way the body
 * was flying reads as momentum rather than as a decal.
 */
function burstLance(world: World, e: EnemyInternal, x: number, y: number): void {
  const a = e.archetype;
  const rng = world.rng;
  const big = a.radius >= 9;
  const n = sparkCount(a.radius);
  const sp2 = e.vel.x * e.vel.x + e.vel.y * e.vel.y;
  const dir = sp2 > 1e-3 ? Math.atan2(e.vel.y, e.vel.x) : e.heading;

  const fwd = Math.round(n * 0.72);
  for (let i = 0; i < fwd; i++) {
    const ang = dir + (rng() * 2 - 1) * 0.5;
    const spd = 90 + rng() * (big ? 300 : 190);
    emit(world, x, y, Math.cos(ang) * spd, Math.sin(ang) * spd,
      0.2 + rng() * 0.34, 1 + rng() * (big ? 2.2 : 1.1),
      a.colour, 'spark', 1.5, 2.0, 18, 0);
  }
  for (let i = fwd; i < n; i++) {
    const ang = dir + Math.PI + (rng() * 2 - 1) * 1.0;
    const spd = 20 + rng() * 70;
    emit(world, x, y, Math.cos(ang) * spd, Math.sin(ang) * spd,
      0.24 + rng() * 0.3, 1 + rng(),
      a.colour, 'spark', 1.1, 3.6, 44, 0);
  }
  for (let i = 0; i < (big ? 8 : 3); i++) {
    const ang = dir + (rng() * 2 - 1) * 1.4;
    const spd = 30 + rng() * 90;
    emit(world, x, y, Math.cos(ang) * spd, Math.sin(ang) * spd,
      0.45 + rng() * 0.5, 1 + rng() * 2,
      a.colour, 'debris', 0.9, 1.5, 90, (rng() * 2 - 1) * 14);
  }
  // Set back along the travel so the ring reads as the body's wake.
  emit(world, x - Math.cos(dir) * a.radius, y - Math.sin(dir) * a.radius,
    0, 0, 0.2, a.radius * 1.3, ROLE.explosion, 'ring', 1.8, 0, 0, 0);
}

/**
 * The body pulls itself in and then lets go. Sparks are born on a shell
 * *outside* the corpse travelling inward on a short fuse, so they converge and
 * die at the centre; the scatter that follows is booked for 110ms later. Two
 * beats where every other variant has one, which is what makes it legible in a
 * crowd.
 */
function burstImplode(world: World, e: EnemyInternal, x: number, y: number): void {
  const a = e.archetype;
  const rng = world.rng;
  const n = sparkCount(a.radius);
  const shell = Math.round(n * 0.5);
  const r0 = a.radius * 3.2;

  for (let i = 0; i < shell; i++) {
    const ang = (i / shell) * TAU + rng() * 0.4;
    const cos = Math.cos(ang), sin = Math.sin(ang);
    const spd = 130 + rng() * 90;
    emit(world, x + cos * r0, y + sin * r0, -cos * spd, -sin * spd,
      0.1 + rng() * 0.09, 1 + rng() * 1.4,
      a.colour, 'spark', 1.7, 0.4, 0, 0);
  }
  // A tightening ring under it, so the collapse has an outline.
  emit(world, x, y, 0, 0, 0.12, r0 * 1.6, a.colour, 'ring', 1.2, 0, 0, 0);
  scheduleBurst(world, 0.11, x, y, n - shell, 60 + a.radius * 16,
    1.4 + a.radius * 0.12, a.colour, Math.round(a.radius * 1.8));
}

/**
 * The sprite comes apart instead of vaporising: `glyph` chips — the same filled
 * square the atlas is drawn out of — tumbling out under gravity, with only
 * enough spark to light the moment. The one variant that leaves something on
 * screen after the flash is gone.
 */
function burstShatter(world: World, e: EnemyInternal, x: number, y: number): void {
  const a = e.archetype;
  const rng = world.rng;
  const n = sparkCount(a.radius);
  const chips = Math.min(11, 4 + Math.round(a.radius * 0.62));

  for (let i = 0; i < chips; i++) {
    const ang = (i / chips) * TAU + (rng() * 2 - 1) * 0.5;
    const spd = 35 + rng() * 110;
    emit(world, x, y, Math.cos(ang) * spd, Math.sin(ang) * spd - 30,
      0.5 + rng() * 0.55, 2 + rng() * (a.radius * 0.28 + 1.6),
      a.colour, 'glyph', 1.2, 0.9, 150, (rng() * 2 - 1) * 9);
  }
  const dust = Math.round(n * 0.45);
  for (let i = 0; i < dust; i++) {
    const ang = rng() * TAU;
    const spd = 25 + rng() * 95;
    emit(world, x, y, Math.cos(ang) * spd, Math.sin(ang) * spd,
      0.18 + rng() * 0.26, 1 + rng(),
      a.colour, 'spark', 1.3, 3.4, 60, 0);
  }
  emit(world, x, y, 0, 0, 0.16, a.radius * 1.2, ROLE.explosion, 'ring', 1.4, 0, 0, 0);
}

/**
 * Three micro-detonations over about 200ms, each thrown off the last one's
 * position. It is the only variant with duration, which is exactly why it earns
 * a slot: in a wave of instant pops, the thing that is still going a fifth of a
 * second later is the thing the eye follows.
 */
function burstCrackle(world: World, e: EnemyInternal, x: number, y: number): void {
  const a = e.archetype;
  const rng = world.rng;
  const n = sparkCount(a.radius);
  const each = Math.max(2, Math.round(n / 3));
  const reach = a.radius * 1.8 + 4;

  for (let i = 0; i < each; i++) {
    const ang = rng() * TAU;
    const spd = 40 + rng() * 130;
    emit(world, x, y, Math.cos(ang) * spd, Math.sin(ang) * spd,
      0.16 + rng() * 0.24, 1 + rng() * 1.3,
      a.colour, 'spark', 1.6, 3.2, 30, 0);
  }
  emit(world, x, y, 0, 0, 0.14, a.radius * 1.1, ROLE.explosion, 'ring', 1.5, 0, 0, 0);

  for (let k = 1; k <= 2; k++) {
    const ang = rng() * TAU;
    const d = reach * (0.5 + rng() * 0.9);
    scheduleBurst(world, 0.07 * k + rng() * 0.03,
      x + Math.cos(ang) * d, y + Math.sin(ang) * d,
      each, 60 + rng() * 90, 1.3, a.colour, k === 2 ? Math.round(a.radius) : 0);
  }
}

/**
 * Small, cheap, over. Swarmlings die in the hundreds and each one is three
 * pixels across; a full shell of shrapnel on every body is a hundred particles
 * a frame spent on something nobody can resolve. Eight sparks and a ring reads
 * the same at that size and costs a third.
 */
function burstPop(world: World, e: EnemyInternal, x: number, y: number): void {
  const a = e.archetype;
  const rng = world.rng;
  const n = Math.max(5, Math.round(sparkCount(a.radius) * 0.42));
  for (let i = 0; i < n; i++) {
    const ang = rng() * TAU;
    const spd = 45 + rng() * 130;
    emit(world, x, y, Math.cos(ang) * spd, Math.sin(ang) * spd,
      0.14 + rng() * 0.24, 1 + rng() * 1.2,
      a.colour, 'spark', 1.5, 3.6, 20, 0);
  }
  emit(world, x, y, 0, 0, 0.15, a.radius * 1.4, a.colour, 'ring', 1.5, 0, 0, 0);
}

/**
 * Splitter children. They inherit the parent's wave scaling so a round-80
 * prime does not shed round-1 motes, and they are pushed slightly back along
 * the track and fanned across the corridor so the burst reads as a burst.
 */
function spawnChildren(parent: EnemyInternal, world: World, track: Track): void {
  const list = parent.archetype.spawnsOnDeath;
  if (!list || parent.depth >= 6) return;
  const rng = world.rng;

  for (const s of list) {
    const child = enemyById(s.id);
    for (let i = 0; i < s.count; i++) {
      const spread = s.count > 1 ? (i / (s.count - 1)) * 2 - 1 : 0;
      const back = 2 + rng() * 10;
      const at = Math.max(0, Math.min(track.length - 1, parent.progress - back));
      const lane = spread * 0.72 + (rng() * 2 - 1) * 0.16;
      const e = acquire(child, world, at, lane, parent.hpScale, parent.depth + 1);
      if (!e) return;
      // Fling them outward from the parent so the split has visible energy.
      const ang = rng() * TAU;
      const kick = 40 + rng() * 70;
      e.vel.x += Math.cos(ang) * kick;
      e.vel.y += Math.sin(ang) * kick;
      e.birth = 0;
    }
  }
}

function leakEnemy(e: EnemyInternal, world: World): void {
  const a = e.archetype;
  world.lives -= a.leak;
  if (world.lives < 0) world.lives = 0;

  const x = leakPos.x, y = leakPos.y;
  const rng = world.rng;
  for (let i = 0; i < 18; i++) {
    const ang = rng() * TAU;
    const spd = 40 + rng() * 130;
    emit(world, x, y, Math.cos(ang) * spd, Math.sin(ang) * spd,
      0.3 + rng() * 0.3, 1 + rng(), ROLE.uiLives, 'smoke', 1.2, 3.0, -20, 0);
  }
  emit(world, x, y, 0, 0, 0.34, 22, ROLE.exitPortal, 'ring', 2.6, 0, 0, 0);
  shock(world, x, y, -40, 80, 0.22);
  world.fx.shake += 1.4 + a.leak * 0.12;
  emitNumber(world, x, y - 8, -a.leak, true);
}

// ────────────────────────────────────────────────────────────────── noise ──

/**
 * Seeded 1-D value noise. `Math.random` would break run reproducibility, and
 * a sine hash bands visibly at these frequencies.
 */
export function noise1(seed: number, t: number): number {
  const i = Math.floor(t);
  const f = t - i;
  const u = f * f * (3 - 2 * f);
  const a = hash2(seed, i);
  const b = hash2(seed, i + 1);
  return a + (b - a) * u;
}

export function hash2(a: number, b: number): number {
  let h = Math.imul(a ^ 0x9e3779b9, 0x85ebca6b);
  h = Math.imul(h ^ (b + 0x165667b1), 0xc2b2ae35);
  h ^= h >>> 15;
  return (h >>> 0) / 4294967296;
}

// ───────────────────────────────────────────────────────────────── system ──

/** Wraps the step for `main.ts`. Must be registered after the flock system. */
export function createEnemySystem(track?: Track): System {
  return {
    name: 'enemies',
    step(dt: number, world: World): void {
      stepEnemies(dt, world, track);
    },
  };
}

/**
 * Hard bound on how far outside the buffer an enemy may travel. Generous,
 * because map entry and exit tails deliberately run off-screen; this only
 * catches an agent a shockwave has thrown into the void.
 */
export const FIELD_MARGIN = 110;

/** Keeps enemies near the buffer if something flings them out. */
export function clampToField(e: Enemy): void {
  const m = FIELD_MARGIN;
  if (e.pos.x < -m) { e.pos.x = -m; if (e.vel.x < 0) e.vel.x = -e.vel.x * 0.4; }
  else if (e.pos.x > VIEW.width + m) { e.pos.x = VIEW.width + m; if (e.vel.x > 0) e.vel.x = -e.vel.x * 0.4; }
  if (e.pos.y < -m) { e.pos.y = -m; if (e.vel.y < 0) e.vel.y = -e.vel.y * 0.4; }
  else if (e.pos.y > VIEW.height + m) { e.pos.y = VIEW.height + m; if (e.vel.y > 0) e.vel.y = -e.vel.y * 0.4; }
}
